import { useState } from 'react';
import {
  ETIQUETA_DE_SEGUIMIENTO, TIPOS_DE_SEGUIMIENTO, type TipoDeSeguimiento, type TurnoDelConsultorio,
} from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { irA, volver } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  AreaTexto, AvisoError, Avatar, Boton, Cargando, Casilla, Entrada, Estrellas, FalloDeCarga, Insignia,
  InsigniaEstadoTurno, Modal, Selector, SelectorEstrellas, Tarjeta, useAvisos, useTitulo,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { decimal, fechaCorta, fechaLarga, hoyIso, pesos, sumarDias } from '../../ui/format';
import { Fila, SelloPaciente, estaCerrado, nombreDe } from './piezas';

const TEXTO_PAGO = { pendiente: 'en revisión', aprobado: 'acreditada', rechazado: 'rechazada', reintegrado: 'devuelta' } as const;

/** Cuánto después se suele hacer cada control; el profesional puede cambiar la fecha. */
const DIAS_SUGERIDOS: Record<TipoDeSeguimiento, number> = { control_anual: 365, limpieza: 180, revision: 30, otro: 90 };

type Accion = (id: number) => Promise<TurnoDelConsultorio>;

export function TurnoDelProfesional({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => consultorio.turno(id), [id]);
  useTitulo(pedido.datos && `Turno de ${nombreDe(pedido.datos.paciente)}`);
  if (pedido.estado === 'cargando' && !pedido.datos) return <SeccionPanel titulo="Turno"><Cargando /></SeccionPanel>;
  if (!pedido.datos) {
    return (
      <SeccionPanel titulo="Turno">
        <FalloDeCarga mensaje={pedido.error ?? 'No encontramos el turno'} alReintentar={pedido.recargar} />
      </SeccionPanel>
    );
  }
  return <Detalle turno={pedido.datos} alCambiar={pedido.recargar} />;
}

function Detalle({ turno: t, alCambiar }: { turno: TurnoDelConsultorio; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [cancelando, setCancelando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const paciente = nombreDe(t.paciente);
  const abierto = t.estado === 'pendiente' || t.estado === 'confirmado';
  const yaEmpezo = new Date(`${t.fecha}T${t.hora_inicio}:00`) <= new Date();

  const ejecutar = async (accion: Accion, exito: string) => {
    setOcupado(true);
    try {
      await accion(t.id_turno);
      mostrarAviso({ tono: 'exito', titulo: exito });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No se pudo actualizar el turno', cuerpo: e instanceof ApiError ? e.message : undefined });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <SeccionPanel titulo={paciente} descripcion={`${t.servicio.nombre} · ${fechaLarga(t.fecha)}, ${t.hora_inicio}`}
      acciones={<Boton tamano="chico" variante="secundario" icono="arrow-left" onClick={() => volver('/pro/turnos')}>Volver</Boton>}>
      <div className="dos-columnas">
        <div className="pila">
          <Tarjeta>
            <header className="tarjeta-titulo">
              <h3>Datos del turno</h3>
              <InsigniaEstadoTurno estado={t.estado} tamano="mediano" />
            </header>
            <dl>
              <Fila icono="info" etiqueta="Código"><span className="codigo">{t.codigo}</span></Fila>
              <Fila icono="stethoscope" etiqueta="Servicio">{t.servicio.nombre}</Fila>
              <Fila icono="calendar" etiqueta="Cuándo">{fechaLarga(t.fecha)}, {t.hora_inicio} a {t.hora_fin}</Fila>
              <Fila icono="map-pin" etiqueta="Modalidad">{t.modalidad === 'videoconsulta' ? 'Videoconsulta' : 'Presencial'}</Fila>
              <Fila icono="wallet" etiqueta="Valor">{pesos(t.precio)}</Fila>
              {t.pago && <Fila icono="credit-card" etiqueta="Seña">{pesos(t.pago.monto)} {TEXTO_PAGO[t.pago.estado]}</Fila>}
              {t.motivo_consulta && <Fila icono="message-circle" etiqueta="Motivo">{t.motivo_consulta}</Fila>}
              {t.motivo_cancelacion && <Fila icono="x" etiqueta="Cancelación">{t.motivo_cancelacion}</Fila>}
            </dl>
            {abierto && (
              <div className="acciones-turno">
                {t.estado === 'pendiente' && (
                  <Boton cargando={ocupado} onClick={() => void ejecutar(consultorio.confirmar, 'Seña acreditada y turno confirmado')}>
                    Acreditar seña
                  </Boton>
                )}
                {t.estado === 'confirmado' && yaEmpezo && (
                  <>
                    <Boton cargando={ocupado} onClick={() => void ejecutar(consultorio.marcarAtendido, 'Turno marcado como atendido')}>
                      Marcar atendido
                    </Boton>
                    <Boton variante="secundario" disabled={ocupado}
                      onClick={() => void ejecutar(consultorio.marcarAusente, 'Turno marcado como ausente')}>
                      No asistió
                    </Boton>
                  </>
                )}
                {!yaEmpezo && <Boton variante="peligro" onClick={() => setCancelando(true)}>Cancelar turno</Boton>}
                {t.estado === 'confirmado' && !yaEmpezo && (
                  <p className="nota">El turno todavía no ocurrió. Vas a poder cerrarlo después del horario.</p>
                )}
              </div>
            )}
          </Tarjeta>
          {t.estado === 'atendido' && <FichaPostAtencion turno={t} alCambiar={alCambiar} />}
          {t.estado === 'atendido' && <SeguimientoDelTurno turno={t} alCambiar={alCambiar} />}
          <Notas turno={t} alCambiar={alCambiar} />
          {t.resena && (
            <Tarjeta>
              <h3 className="titulo-tarjeta">Lo que dijo el paciente de vos</h3>
              <Estrellas puntaje={t.resena.calificacion} tamano={16} />
              {t.resena.comentario && <p className="texto-resena">{t.resena.comentario}</p>}
            </Tarjeta>
          )}
        </div>

        <div className="pila">
          <Tarjeta>
            <div className="tarjeta-paciente">
              <Avatar nombre={paciente} tamano={48} decorativo />
              <div>
                <strong>{paciente}</strong>
                <span>{t.paciente.obra_social ?? 'Particular'} · {t.paciente.telefono}</span>
              </div>
            </div>
            <div className="chips">
              <SelloPaciente reputacion={t.reputacion} />
              {t.reputacion.puntaje !== null && <Insignia tono="neutral" tamano="chico" icono="star">{decimal(t.reputacion.puntaje)} de 5</Insignia>}
            </div>
            <div className="numeros-paciente">
              <div><strong>{t.reputacion.turnos_cerrados}</strong><span>turnos cerrados</span></div>
              <div><strong>{t.reputacion.ausencias}</strong><span>ausencias</span></div>
              <div><strong>{t.reputacion.tasa_ausencia}%</strong><span>tasa de ausencia</span></div>
            </div>
            <Boton anchoCompleto variante="secundario" onClick={() => irA(`/pro/paciente/${t.paciente.id_paciente}`)}>
              Ver ficha completa
            </Boton>
          </Tarjeta>
          <CalificacionDelPaciente turno={t} alCambiar={alCambiar} />
        </div>
      </div>
      {cancelando && <ModalCancelacion turno={t} alCerrar={() => setCancelando(false)} alCancelar={alCambiar} />}
    </SeccionPanel>
  );
}

function CalificacionDelPaciente({ turno, alCambiar }: { turno: TurnoDelConsultorio; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [puntaje, setPuntaje] = useState(0);
  const [puntual, setPuntual] = useState(turno.estado !== 'ausente');
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const c = turno.calificacion;

  if (c) {
    return (
      <Tarjeta>
        <h3 className="titulo-tarjeta">Tu calificación al paciente</h3>
        <div className="calificacion-hecha">
          <Estrellas puntaje={c.puntaje} tamano={17} />
          <span>{c.puntual ? 'Llegó puntual' : turno.estado === 'ausente' ? 'No asistió' : 'Llegó tarde'}</span>
        </div>
        {c.nota_interna && <p className="texto-resena">{c.nota_interna}</p>}
        <p className="nota">El paciente no ve tu nota. Sí cuenta para su puntaje promedio.</p>
      </Tarjeta>
    );
  }
  if (!estaCerrado(turno)) {
    return (
      <Tarjeta>
        <h3 className="titulo-tarjeta">Calificar al paciente</h3>
        <p className="nota">Vas a poder calificarlo cuando cierres el turno como atendido o como ausente.</p>
      </Tarjeta>
    );
  }

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.calificar(turno.id_turno, { puntaje, puntual, nota_interna: nota.trim() || undefined });
      mostrarAviso({ tono: 'exito', titulo: 'Calificación guardada', cuerpo: 'Suma al historial del paciente.' });
      alCambiar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar la calificación. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Calificar al paciente</h3>
      <p className="nota">Sirve para que el próximo profesional sepa con quién se compromete. Tu nota es interna: el paciente no la ve.</p>
      <div className="formulario">
        <SelectorEstrellas valor={puntaje} alCambiar={setPuntaje} etiqueta="Puntaje del paciente" />
        {turno.estado === 'atendido' && (
          <Casilla checked={puntual} onChange={(e) => setPuntual(e.target.checked)} etiqueta="Llegó puntual" />
        )}
        <AreaTexto etiqueta="Nota interna (opcional)" rows={3} maxLength={300} value={nota}
          placeholder="Llegó puntual y con los estudios que le pedí." onChange={(e) => setNota(e.target.value)} />
        <AvisoError>{error}</AvisoError>
        <Boton anchoCompleto disabled={puntaje === 0} cargando={guardando} onClick={() => void guardar()}>Guardar calificación</Boton>
      </div>
    </Tarjeta>
  );
}

function FichaPostAtencion({ turno, alCambiar }: { turno: TurnoDelConsultorio; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  if (turno.ficha) {
    return (
      <Tarjeta>
        <h3 className="titulo-tarjeta">Indicaciones que le dejaste</h3>
        <p className="texto-resena">{turno.ficha.instrucciones}</p>
        <p className="nota">Emitidas el {fechaCorta(turno.ficha.fecha.slice(0, 10))}.</p>
      </Tarjeta>
    );
  }

  const emitir = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.emitirFicha(turno.id_turno, texto);
      mostrarAviso({ tono: 'exito', titulo: 'Indicaciones guardadas', cuerpo: 'Quedan en el turno y en la ficha del paciente.' });
      alCambiar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar las indicaciones. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Indicaciones post atención</h3>
      <div className="formulario">
        <AreaTexto etiqueta="Qué tiene que hacer el paciente" rows={4} maxLength={2000} value={texto}
          placeholder="Aplicar la crema dos veces por día durante 15 días y volver a control." onChange={(e) => setTexto(e.target.value)} />
        <AvisoError>{error}</AvisoError>
        <Boton disabled={texto.trim().length < 10} cargando={guardando} onClick={() => void emitir()}>Guardar indicaciones</Boton>
      </div>
    </Tarjeta>
  );
}

function SeguimientoDelTurno({ turno, alCambiar }: { turno: TurnoDelConsultorio; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [tipo, setTipo] = useState<TipoDeSeguimiento>('control_anual');
  const [fecha, setFecha] = useState(() => sumarDias(hoyIso(), DIAS_SUGERIDOS.control_anual));
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const programado = turno.seguimientos.find((s) => s.estado === 'programado');

  if (programado) {
    return (
      <Tarjeta>
        <h3 className="titulo-tarjeta">Seguimiento programado</h3>
        <p className="texto-resena">{ETIQUETA_DE_SEGUIMIENTO[programado.tipo]} para el {fechaLarga(programado.fecha_programada)}.</p>
        <p className="nota">
          A {turno.paciente.nombre} le llega el recordatorio por WhatsApp ese día, y lo ve en su inicio desde dos semanas antes.
        </p>
      </Tarjeta>
    );
  }

  const programar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.programarSeguimiento(turno.id_turno, { tipo, fecha_programada: fecha });
      mostrarAviso({ tono: 'exito', titulo: 'Seguimiento programado', cuerpo: `Le avisamos a ${turno.paciente.nombre} el ${fechaCorta(fecha)}.` });
      alCambiar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos programar el seguimiento. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Programar seguimiento</h3>
      <p className="nota">Si el paciente tiene que volver, dejale un recordatorio. Le llega por WhatsApp el día que elijas.</p>
      <div className="formulario">
        <div className="formulario__fila">
          <Selector etiqueta="Control" value={tipo}
            onChange={(e) => {
              const elegido = e.target.value as TipoDeSeguimiento;
              setTipo(elegido);
              setFecha(sumarDias(hoyIso(), DIAS_SUGERIDOS[elegido]));
            }}
            opciones={TIPOS_DE_SEGUIMIENTO.map((s) => ({ valor: s, texto: ETIQUETA_DE_SEGUIMIENTO[s] }))} />
          <Entrada etiqueta="Fecha" type="date" min={sumarDias(hoyIso(), 1)} max={sumarDias(hoyIso(), 730)} value={fecha}
            onChange={(e) => setFecha(e.target.value)} />
        </div>
        <AvisoError>{error}</AvisoError>
        <Boton variante="secundario" disabled={!fecha} cargando={guardando} onClick={() => void programar()}>
          Programar recordatorio
        </Boton>
      </div>
    </Tarjeta>
  );
}

function Notas({ turno, alCambiar }: { turno: TurnoDelConsultorio; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);

  const anotar = async () => {
    setGuardando(true);
    try {
      await consultorio.anotar(turno.id_turno, texto);
      setTexto('');
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No pudimos guardar la nota', cuerpo: e instanceof ApiError ? e.message : undefined });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Notas clínicas</h3>
      {turno.notas.length > 0 && (
        <ul className="notas">
          {turno.notas.map((n) => <li key={n.id_nota}><span>{fechaCorta(n.fecha.slice(0, 10))}</span>{n.texto}</li>)}
        </ul>
      )}
      <div className="nota-nueva">
        <Entrada aria-label="Nueva nota" placeholder="Agregá una nota privada" maxLength={2000} value={texto}
          onChange={(e) => setTexto(e.target.value)} />
        <Boton variante="secundario" disabled={texto.trim().length < 3} cargando={guardando} onClick={() => void anotar()}>
          Guardar
        </Boton>
      </div>
    </Tarjeta>
  );
}

function ModalCancelacion({ turno, alCerrar, alCancelar }: { turno: TurnoDelConsultorio; alCerrar: () => void; alCancelar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cancelar = async () => {
    setEnviando(true);
    try {
      const { reintegro } = await consultorio.cancelar(turno.id_turno, motivo);
      mostrarAviso({ tono: 'exito', titulo: 'Turno cancelado',
        cuerpo: reintegro ? 'Le devolvemos la seña al paciente y le avisamos por WhatsApp.' : 'Le avisamos al paciente por WhatsApp.' });
      alCerrar();
      alCancelar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos cancelar el turno. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal abierto alCerrar={alCerrar} titulo="Cancelar el turno"
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Volver</Boton>
        <Boton variante="peligro" disabled={motivo.trim().length < 5} cargando={enviando} onClick={() => void cancelar()}>
          Confirmar cancelación
        </Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <p className="nota">
        Si cancelás vos, la seña se le devuelve siempre a {turno.paciente.nombre}. Contale el motivo: le llega con el aviso.
      </p>
      <AreaTexto etiqueta="Motivo" rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
    </Modal>
  );
}
